/**
 * useSeo — per-route SEO for the public /tools pages.
 *
 * The app is a client-rendered SPA with one static <title> in index.html, so
 * public pages that need to rank set their own title, meta description,
 * canonical link, and JSON-LD structured data at mount and restore the
 * defaults at unmount. Googlebot renders JS, so this is enough for indexing
 * without adding a head-manager dependency or a prerender step.
 */
import { useEffect } from 'react';

// Canonical home of the tools content is the marketing site (SEO surface).
export const TOOLS_ORIGIN = 'https://anterotrail.com';

const DEFAULT_TITLE = 'AI Estimating Assistant | Antero Trail';

interface SeoOptions {
  title: string;
  description: string;
  /** Path only, e.g. "/tools/crane-cost-estimator". */
  path: string;
  /** JSON-LD objects to inject as application/ld+json scripts. */
  jsonLd?: object[];
}

export function useSeo({ title, description, path, jsonLd }: SeoOptions) {
  useEffect(() => {
    document.title = title;

    const meta = document.querySelector<HTMLMetaElement>(
      'meta[name="description"]',
    );
    const prevDescription = meta?.content ?? '';
    if (meta) meta.content = description;

    let canonical = document.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]',
    );
    const createdCanonical = !canonical;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    const prevCanonical = canonical.href;
    canonical.href = `${TOOLS_ORIGIN}${path}`;

    const scripts = (jsonLd ?? []).map((obj) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
      return s;
    });

    return () => {
      document.title = DEFAULT_TITLE;
      if (meta) meta.content = prevDescription;
      if (canonical) {
        if (createdCanonical) canonical.remove();
        else canonical.href = prevCanonical;
      }
      scripts.forEach((s) => s.remove());
    };
    // Reason: jsonLd is page-static; serializing it keeps the deps primitive
    // so callers can pass inline literals without memoizing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, JSON.stringify(jsonLd ?? [])]);
}

/** FAQPage JSON-LD from the page's visible FAQ items (must match on-page text). */
export function faqJsonLd(items: { q: string; a: string }[]): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((i) => ({
      '@type': 'Question',
      name: i.q,
      acceptedAnswer: { '@type': 'Answer', text: i.a },
    })),
  };
}

/** WebApplication JSON-LD for a free calculator page. */
export function calculatorJsonLd(opts: {
  name: string;
  description: string;
  path: string;
}): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: opts.name,
    description: opts.description,
    url: `${TOOLS_ORIGIN}${opts.path}`,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    publisher: {
      '@type': 'Organization',
      name: 'Antero Trail',
      url: 'https://anterotrail.com',
    },
  };
}
